import { useState } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CustomSpinner from '../CustomSpinner';
import { resolveCssVarValue } from "../../utils/CommonFunction";

/**
 * Common ExportButton component
 * @param {Object} props
 * @param {Array<{label: string, value: string}>} props.options - Export format options
 * @param {function} props.onExport - Function(format) called on export
 * @param {boolean} props.loading - Loading state
 * @param {string} [props.title] - Button label
 * @param {object} [props.buttonProps] - Extra props for Button
 */
export default function ExportButton({ options, onExport, loading, title = "Export", buttonProps }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuWidth, setMenuWidth] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuWidth(event.currentTarget?.offsetWidth || null);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSelect = (format) => {
    handleClose();
    onExport && onExport(format);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        data-export-button="true"
        endIcon={loading ? null : <ArrowDropDownIcon />}
        startIcon={loading ? null : <UploadFileOutlinedIcon />}
        onClick={handleClick}
        disabled={loading}
        sx= {{
                textTransform: 'capitalize',
                fontWeight: 700,
                minWidth: 'calc(var(--fs-hero)*3.537)',
                height: 'calc(var(--fs-xl)*2.35) !important',
                maxWidth: 'calc(var(--fs-hero)*4.5)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderRadius: 'var(--sp-md2)',
                fontSize: 'calc(var(--fs-sm2) * 1.1)',
                backgroundColor: '#ffffff !important',
                border: '1.5px solid #e5e7eb !important',
                color: '#374151 !important',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                '&:hover': {
                  backgroundColor: '#f3f4f6 !important',
                  borderColor: '#d1d5db !important',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)'
                },
                '& .MuiSvgIcon-root': {
                  height: 'calc(var(--fs-lg) * 1.3)',
                  width: 'calc(var(--fs-lg) * 1.3)',
                  marginRight: 'calc(var(--sp-xxs)*2)',
                  transition: 'all 0.2s ease'
                },
                '&:hover .MuiSvgIcon-root': {
                  transform: 'translateX(2px)'
                }
              }}
      >
        {loading ? <CustomSpinner size={resolveCssVarValue('--fs-xl')*1.132075} /> : title}
      </Button>
      <Menu 
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: menuWidth ? `${menuWidth}px` : undefined,
          }
        }}
      >
        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleSelect(opt.value)}
            sx={{ justifyContent: 'flex-start', textAlign: 'center', fontSize: 'calc(var(--fs-xs) * 1.6)', display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            {opt.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{opt.icon}</span>}
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
